import { useState, useEffect, useMemo, useRef } from "react";
import "../styles/help.css";
import MainFooter from "../components/MainFooter"; // remove if your layout already renders the footer
import MainHeader from "../components/MainHeader";


/* ─── Data ────────────────────────────────────────────────────── */
const HELP_CARDS = [
  {
    id: "how_to_create_event",
    icon: "fas fa-calendar-plus",
    title: "How to create event",
    description: "Set up a new event with details, date, and location.",
    keywords: "create event new organize host publish ticket",
  },
  {
    id: "how_to_register_as_attendee",
    icon: "fas fa-user-check",
    title: "Register as attendee",
    description: "Sign up to attend an event as a participant.",
    keywords: "attend attendee register join participant ticket",
  },
  {
    id: "how_to_register_as_speaker",
    icon: "fas fa-microphone-alt",
    title: "Register as speaker",
    description: "Apply to speak at an event and share your expertise.",
    keywords: "speaker speak talk apply presentation stage",
  },
  {
    id: "how_to_get_sharable_link",
    icon: "fas fa-link",
    title: "How to get sharable link",
    description: "Generate a link to share your event or profile with others.",
    keywords: "share link url copy invite generate",
  },
  {
    id: "how_to_sign_up",
    icon: "fas fa-sign-in-alt",
    title: "How to sign in",
    description: "Access your account using email, Google, or other methods.",
    keywords: "login signin sign in account password email google",
  },
];

const STATS = [
  [String(HELP_CARDS.length), "Step-by-step guides"],
  ["24/7", "Self-service access"],
  ["< 24h", "Support response"],
];

/* ─── Helpers ─────────────────────────────────────────────────── */
/** Wrap matches of `term` inside `text` with <mark>. */
function highlight(text, term) {
  if (!term) return text;
  const pattern = new RegExp(
    `(${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
    "gi"
  );
  // With a capture group, split() puts the matches at odd indexes
  return text
    .split(pattern)
    .map((part, i) => (i % 2 === 1 ? <mark key={i}>{part}</mark> : part));
}

/* ─── Sub-component: one guide "ticket" ───────────────────────── */
function HelpCard({ card, href, term }) {
  return (
    <a href={href} className="help-card" data-help-id={card.id}>
      <div className="help-card__body">
        <div className="card-icon">
          <i className={card.icon} aria-hidden="true" />
        </div>
        <h3>{highlight(card.title, term)}</h3>
        <p>{highlight(card.description, term)}</p>
      </div>
      <div className="help-card__stub">
        <span className="card-link">View steps</span>
        <span className="card-go">
          <i className="fas fa-arrow-right" aria-hidden="true" />
        </span>
      </div>
    </a>
  );
}

/* ─── Main component ──────────────────────────────────────────── */
/**
 * Props (all optional):
 *  - getHref(id)    → URL of each step page. Default: `/${id}`
 *  - homeUrl        → breadcrumb "Home" link. Default: "/"
 *  - supportEmail   → email used by the support CTA
 */
export default function Help({
  getHref = (id) => `/${id}`,
  homeUrl = "/",
  supportEmail = "info@eventfork.com",
}) {
  const [search, setSearch] = useState("");
  const [scrollPct, setScrollPct] = useState(0);
  const [animateIn, setAnimateIn] = useState(true);
  const inputRef = useRef(null);

  const term = search.trim();
  const query = term.toLowerCase();

  const results = useMemo(
    () =>
      HELP_CARDS.filter(
        (card) =>
          !query ||
          card.title.toLowerCase().includes(query) ||
          card.description.toLowerCase().includes(query) ||
          card.keywords.toLowerCase().includes(query)
      ),
    [query]
  );

  /* Scroll progress bar */
  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - doc.clientHeight;
      setScrollPct(max > 0 ? (window.scrollY / max) * 100 : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* Play the card entrance once, then drop the class */
  useEffect(() => {
    const t = setTimeout(() => setAnimateIn(false), 900);
    return () => clearTimeout(t);
  }, []);

  /* Press "/" anywhere to focus search */
  useEffect(() => {
    const onKey = (e) => {
      const tag = document.activeElement?.tagName;
      if (e.key === "/" && tag !== "INPUT" && tag !== "TEXTAREA") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const resetSearch = () => {
    setSearch("");
    inputRef.current?.focus();
  };

  return (

    <div className="help-page">
      <div
        className="help-progress"
        style={{ width: `${scrollPct}%` }}
        aria-hidden="true"
      />
      <MainHeader />
      {/* ── Compact header: breadcrumb + title left, search right ── */}
      <header className="help-hero">
        <div className="help-hero__inner">
          <div className="help-hero__text">
            <nav className="help-breadcrumb" aria-label="Breadcrumb">
              <a href={homeUrl}>Home</a>
              <span aria-hidden="true">/</span>
              <span>Support</span>
              <span aria-hidden="true">/</span>
              <span aria-current="page">Help Center</span>
            </nav>
            <h1>How can we help?</h1>
            <p className="help-hero__sub">
              Guides for signing in, creating events, and joining them on
              Eventfork.com.
            </p>
          </div>

          <div
            className={`help-search${term ? " has-value" : ""}`}
            role="search"
          >
            <i className="fas fa-search" aria-hidden="true" />
            <input
              ref={inputRef}
              id="help-search-input"
              type="text"
              placeholder="Search guides, e.g. 'create event'"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape" && search) {
                  e.preventDefault();
                  resetSearch();
                }
              }}
              aria-label="Search help guides"
              autoComplete="off"
              spellCheck={false}
            />
            <div className="help-search-end">
              {!term && (
                <span className="help-kbd" aria-hidden="true">/</span>
              )}
              {term && (
                <button
                  type="button"
                  id="help-search-clear"
                  onClick={resetSearch}
                  aria-label="Clear search"
                >
                  <i className="fas fa-times" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="help-main">
        {/* ── Slim stats bar ── */}
        <div className="help-stats">
          {STATS.map(([num, label]) => (
            <div className="stat" key={label}>
              <strong>{num}</strong>
              <span>{label}</span>
            </div>
          ))}
        </div>

        <p className="help-results-meta" aria-live="polite">
          {term &&
            `${results.length} ${results.length === 1 ? "guide" : "guides"} found for "${term}"`}
        </p>

        {results.length > 0 ? (
          <div
            className={`cards-grid${term ? " is-filtering" : ""}${animateIn ? " animate-in" : ""
              }`}
          >
            {results.map((card) => (
              <HelpCard
                key={card.id}
                card={card}
                href={getHref(card.id)}
                term={term}
              />
            ))}
          </div>
        ) : (
          <div className="no-results" role="status">
            <i className="fas fa-search" aria-hidden="true" />
            <strong>No guides match your search</strong>
            <p>
              Try a broader word like "event" or "sign in", or browse all
              guides.
            </p>
            <button type="button" onClick={resetSearch}>
              Show all guides
            </button>
          </div>
        )}

        {/* ── Support CTA ── */}

        <section className="help-cta" aria-label="Contact support">
          <div>
            <h2>Didn't find what you were looking for?</h2>
            <p>Email us and we'll reply within 24 hours.</p>
          </div>

          <a
            href={`https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(supportEmail)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Contact support
          </a>
        </section>
      </main>

      <MainFooter />
    </div>
  );
}